import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PhoneCall, Users, CalendarCheck, BarChart3 } from 'lucide-react';
import { Reveal, SectionHeading } from '../components/Reveal';
import { CallInterface, LeadCard, AppointmentCard, MiniAnalytics, Waveform } from '../components/mockui';

const TABS = [
  {
    id: 'calls',
    label: 'AI Voice Calls',
    icon: <PhoneCall className="h-4 w-4" />,
    title: 'Every call answered by your AI assistant',
    copy: 'Respond to every caller instantly. AI handles multiple calls at once without wait times or missed opportunities — with a natural, human-like voice your customers trust.',
  },
  {
    id: 'leads',
    label: 'Lead Capture',
    icon: <Users className="h-4 w-4" />,
    title: 'Leads captured automatically',
    copy: 'Every conversation turns into a lead card with contact details and context — saved to your dashboard the moment the call ends.',
  },
  {
    id: 'appointments',
    label: 'Appointments',
    icon: <CalendarCheck className="h-4 w-4" />,
    title: 'Appointments booked on the call',
    copy: 'CapableBee books appointments and routes calls while chatting with your customer — synced with your calendar and workflow.',
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: <BarChart3 className="h-4 w-4" />,
    title: 'Track performance in real time',
    copy: 'Customize the dashboard display according to your needs and watch calls, leads and outcomes in one clean view.',
  },
];

export const Showcase = () => {
  const [tab, setTab] = useState('calls');
  const active = TABS.find((t) => t.id === tab);

  return (
    <section className="bg-brand-iceSoft py-20 lg:py-28" data-testid="showcase-section">
      <div className="container-x">
        <SectionHeading
          eyebrow="Product Experience"
          title="A real production workspace for your AI voice agent"
          center
        />

        <Reveal delay={0.1}>
          <div
            role="tablist"
            aria-label="Product capabilities"
            className="mx-auto mt-10 flex w-fit max-w-full flex-wrap justify-center gap-1 rounded-full border border-brand-line bg-white p-1.5 shadow-card"
            data-testid="showcase-tabs"
          >
            {TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                data-testid={`showcase-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-all ${
                  tab === t.id ? 'bg-brand-navy text-white shadow-soft' : 'text-brand-body hover:bg-brand-ice'
                }`}
              >
                {t.icon}
                {t.label}
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-10 grid items-center gap-10 rounded-[2rem] border border-brand-line bg-white p-6 shadow-soft sm:p-10 lg:grid-cols-2 lg:gap-14">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`showcase-panel-${active.id}`}
              >
                {active.id === 'calls' && <CallInterface />}
                {active.id === 'leads' && (
                  <div className="space-y-3" aria-hidden="true">
                    <LeadCard name="Sarah Miller" email="sarah@brightside.io" initials="SM" />
                    <LeadCard name="James Okafor" email="james@nextflow.co" initials="JO" tag="Follow-up sent" />
                    <LeadCard name="Priya Nair" email="priya@studio.io" initials="PN" tag="Qualified" />
                  </div>
                )}
                {active.id === 'appointments' && (
                  <div className="space-y-3" aria-hidden="true">
                    <AppointmentCard title="Thu, 2:30 PM" meta="Dental cleaning · Confirmed" />
                    <AppointmentCard title="Fri, 10:00 AM" meta="Consultation call · Confirmed" tone="emerald" />
                    <AppointmentCard title="Mon, 4:15 PM" meta="Follow-up call · Scheduled" />
                  </div>
                )}
                {active.id === 'analytics' && (
                  <div className="space-y-4" aria-hidden="true">
                    <MiniAnalytics />
                    <div className="flex items-center justify-between rounded-2xl border border-brand-line bg-white p-4 shadow-card">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Time saved</p>
                        <p className="text-2xl font-extrabold text-brand-navy">80%</p>
                      </div>
                      <Waveform bars={10} className="h-8" />
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            <div>
              <h3 className="text-2xl font-bold tracking-tight text-brand-navy sm:text-3xl">{active.title}</h3>
              <p className="mt-4 text-base leading-relaxed text-slate-600">{active.copy}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {TABS.map((t) => (
                  <span
                    key={t.id}
                    className={`h-1.5 rounded-full transition-all ${tab === t.id ? 'w-8 bg-brand-sky' : 'w-3 bg-brand-line'}`}
                    aria-hidden="true"
                  />
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
